import React, { useState } from 'react';
import { X, Download, Users, Target, Award, Star, Brain, Heart, Shield, Globe, FileText, CheckCircle, Lightbulb, MessageSquare, Building, Cpu, BarChart3, Calendar, DollarSign, BookOpen, Sparkles } from 'lucide-react';

interface FicheEmploiPrimeProps {
  isOpen: boolean;
  onClose: () => void;
}

const FicheEmploiPrime: React.FC<FicheEmploiPrimeProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('resume');

  if (!isOpen) return null;

  const handleExportPDF = () => {
    // Fonctionnalité d'export PDF
    window.print();
  };

  const tabs = [
    { id: 'resume', label: 'Résumé', icon: FileText },
    { id: 'responsabilites', label: 'Responsabilités', icon: Target },
    { id: 'taches', label: 'Tâches', icon: CheckCircle },
    { id: 'exigences', label: 'Exigences', icon: Award },
    { id: 'softskills', label: 'Soft Skills', icon: Heart }
  ];

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header Pro avec Badge Urgent */}
        <div className="sticky top-0 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 text-white p-8 relative overflow-hidden">
          {/* Pattern de fond */}
          <div className="absolute inset-0 bg-grid-white/10 pointer-events-none" />
          
          {/* Badge URGENT */}
          <div className="absolute top-4 left-4 bg-red-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-xl animate-pulse flex items-center gap-2">
            <span className="w-2 h-2 bg-white rounded-full animate-ping" />
            URGENT: 2 POSTES DISPONIBLES
          </div>

          <div className="relative flex items-center justify-between mt-8">
            <div className="flex-1">
              <h2 className="text-4xl font-black flex items-center gap-3 mb-3">
                <Star className="w-10 h-10 text-yellow-300 drop-shadow-lg" />
                🚀 OPPORTUNITÉ EXCEPTIONNELLE
              </h2>
              <h3 className="text-2xl font-bold text-yellow-300 mb-2">Architecte de l'Excellence TI</h3>
              <p className="text-blue-100 text-lg font-medium max-w-2xl">✨ Rejoignez l'Élite des Conseillers Technologiques - Transformez des Vies, Créez l'Impact ✨</p>
              
              {/* Stats Mini dans Header */}
              <div className="flex gap-6 mt-4">
                <div className="bg-white/20 backdrop-blur-md rounded-xl px-4 py-2 border border-white/30">
                  <div className="text-2xl font-black">100K-5M$</div>
                  <div className="text-xs text-blue-100">Budget géré</div>
                </div>
                <div className="bg-white/20 backdrop-blur-md rounded-xl px-4 py-2 border border-white/30">
                  <div className="text-2xl font-black">15+</div>
                  <div className="text-xs text-blue-100">Clients</div>
                </div>
                <div className="bg-white/20 backdrop-blur-md rounded-xl px-4 py-2 border border-white/30">
                  <div className="text-2xl font-black">∞</div>
                  <div className="text-xs text-blue-100">Impact</div>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <button
                onClick={handleExportPDF}
                className="flex items-center gap-2 px-6 py-3 bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold rounded-xl transition-all shadow-xl hover:shadow-2xl hover:scale-105"
              >
                <Download className="w-5 h-5" />
                Export PDF
              </button>
              <button
                onClick={onClose}
                className="p-3 hover:bg-white/20 rounded-xl transition-colors backdrop-blur-sm border border-white/30"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Navigation Tabs Pro */}
        <div className="sticky top-20 bg-white border-b-2 border-gray-100 px-8 py-4 shadow-sm z-10">
          <div className="flex space-x-2 bg-gradient-to-r from-gray-50 to-blue-50 p-2 rounded-xl border border-gray-200">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg transition-all font-semibold ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg scale-105'
                      : 'text-gray-600 hover:text-blue-600 hover:bg-white'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
        <div className="p-8">
          {activeTab === 'resume' && (
            <div className="space-y-8">
              {/* Badges Compétences Clés */}
              <div className="flex flex-wrap gap-3 justify-center">
                <span className="px-5 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-full font-bold text-sm shadow-lg">
                  💎 Leadership Stratégique
                </span>
                <span className="px-5 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-full font-bold text-sm shadow-lg">
                  🧠 Innovation IA
                </span>
                <span className="px-5 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-full font-bold text-sm shadow-lg">
                  🏛️ Gouvernance ITIL/NIST
                </span>
                <span className="px-5 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-full font-bold text-sm shadow-lg">
                  🚀 Transformation Digitale
                </span>
                <span className="px-5 py-2 bg-gradient-to-r from-pink-500 to-pink-600 text-white rounded-full font-bold text-sm shadow-lg">
                  ❤️ Intelligence Émotionnelle
                </span>
              </div>

              <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-8 rounded-2xl border-2 border-blue-200 shadow-lg">
                <h3 className="text-2xl font-black text-blue-900 mb-6 flex items-center gap-3">
                  <FileText className="w-8 h-8 text-blue-600" />
                  🎯 VOTRE MISSION : Devenir un Héros de la Transformation Digitale
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl border-2 border-blue-200">
                    <h4 className="font-bold text-gray-800 mb-4 text-lg">🌟 Votre Rôle de Super-Héros</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Vous êtes un <strong>Architecte de l'Excellence TI</strong> qui transforme des défis en 
                      <span className="text-blue-600 font-semibold"> victoires client</span>, crée des 
                      <span className="text-green-600 font-semibold"> innovations révolutionnaires</span> et bâtit des 
                      <span className="text-purple-600 font-semibold"> empires technologiques</span>. 
                      Vous ne vendez pas des services, vous <strong>créez des légendes</strong> et <strong>changez des vies</strong>.
                    </p>
                  </div>
                  <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl border-2 border-purple-200">
                    <h4 className="font-bold text-gray-800 mb-4 text-lg">🔥 Pourquoi BZ est VOTRE Destination de Rêve</h4>
                    <ul className="text-gray-700 space-y-3">
                      <li className="flex items-start gap-3">
                        <Sparkles className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Innovation:</strong> Vous serez le <em>Visionnaire</em> qui anticipe l'avenir</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Users className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Relations Client:</strong> Vous deviendrez leur <em>Héros de confiance</em></span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Shield className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Gouvernance:</strong> Vous bâtirez des <em>Empires de Processus</em></span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Heart className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Qualité Humaine:</strong> Votre <em>Super-Pouvoir</em> : L'Intelligence Émotionnelle</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Stats XXL Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="relative p-6 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl shadow-xl overflow-hidden group hover:scale-105 transition-transform">
                  <div className="absolute inset-0 bg-grid-white/10 pointer-events-none" />
                  <div className="relative">
                    <Building className="w-10 h-10 text-white/80 mb-3" />
                    <h5 className="font-black text-white text-xl mb-2">🏢 Votre Royaume</h5>
                    <p className="text-white/90 font-medium">Des PME aux <strong className="text-yellow-300">Géants de l'Industrie</strong></p>
                    <p className="text-white/70 text-sm mt-2">Tous secteurs, tous défis</p>
                  </div>
                </div>
                
                <div className="relative p-6 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl shadow-xl overflow-hidden group hover:scale-105 transition-transform">
                  <div className="absolute inset-0 bg-grid-white/10 pointer-events-none" />
                  <div className="relative">
                    <DollarSign className="w-10 h-10 text-white/80 mb-3" />
                    <h5 className="font-black text-white text-xl mb-2">💰 Impact Financier</h5>
                    <p className="text-white/90 font-black text-2xl">100K$ à 5M$+</p>
                    <p className="text-white/70 text-sm mt-2">Votre influence est colossale</p>
                  </div>
                </div>
                
                <div className="relative p-6 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl shadow-xl overflow-hidden group hover:scale-105 transition-transform">
                  <div className="absolute inset-0 bg-grid-white/10 pointer-events-none" />
                  <div className="relative">
                    <Globe className="w-10 h-10 text-white/80 mb-3" />
                    <h5 className="font-black text-white text-xl mb-2">🌍 Portée Mondiale</h5>
                    <p className="text-white/90 font-medium">Local, national et <strong className="text-yellow-300">international</strong></p>
                    <p className="text-white/70 text-sm mt-2">Impact traverse les frontières</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'responsabilites' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl border-l-4 border-green-500">
                <h3 className="text-xl font-bold text-green-800 mb-4 flex items-center gap-2">
                  <Target className="w-6 h-6 text-green-600" />
                  🚀 VOS SUPERPOUVOIRS : Ce qui Vous Attend
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="p-4 bg-white rounded-lg shadow-sm">
                      <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                        <Users className="w-5 h-5 text-blue-600" />
                        💎 Maître des Relations Client
                      </h4>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• Vous devenez le <strong>Conseiller de Confiance</strong> des PDG</li>
                        <li>• Vous créez des <strong>Liens Éternels</strong> avec vos clients</li>
                        <li>• Vous lisez dans leurs <strong>Âmes Business</strong></li>
                        <li>• Vous transformez la satisfaction en <strong>Adoration</strong></li>
                      </ul>
                    </div>
                    <div className="p-4 bg-white rounded-lg shadow-sm">
                      <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                        <Brain className="w-5 h-5 text-purple-600" />
                        🧠 Génie de l'Innovation
                      </h4>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• Vous <strong>Anticipez l'Avenir</strong> avant qu'il n'arrive</li>
                        <li>• Vous <strong>Créez des Révélations</strong> technologiques</li>
                        <li>• Vous <strong>Révolutionnez</strong> les solutions existantes</li>
                        <li>• Vous <strong>Domptez l'IA</strong> pour changer le monde</li>
                      </ul>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="p-4 bg-white rounded-lg shadow-sm">
                      <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                        <Shield className="w-5 h-5 text-green-600" />
                        🏛️ Architecte de l'Empire
                      </h4>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• Vous <strong>Construisez des Dynasties</strong> de comités TI</li>
                        <li>• Vous <strong>Créez des Systèmes</strong> parfaits</li>
                        <li>• Vous <strong>Maîtrisez les Standards</strong> (NIST, ITIL)</li>
                        <li>• Vous <strong>Protégez des Royaumes</strong> entiers</li>
                      </ul>
                    </div>
                    <div className="p-4 bg-white rounded-lg shadow-sm">
                      <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-orange-600" />
                        📈 Maître Stratège
                      </h4>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• Vous <strong>Dessinez l'Avenir</strong> avec vos roadmaps</li>
                        <li>• Vous <strong>Orchestrez des Fortunes</strong> budgétaires</li>
                        <li>• Vous <strong>Créez des Métriques</strong> de légende</li>
                        <li>• Vous <strong>Harmonisez</strong> business et TI</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'taches' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-6 rounded-xl border-l-4 border-blue-500">
                <h3 className="text-xl font-bold text-blue-800 mb-4 flex items-center gap-2">
                  <CheckCircle className="w-6 h-6 text-blue-600" />
                  Tâches Quotidiennes
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-blue-600" />
                      Réunions & Communication
                    </h4>
                    <div className="space-y-3">
                      <div className="p-3 bg-white rounded-lg shadow-sm">
                        <div className="font-medium text-gray-800 text-sm">Réunions Client (2-3/jour)</div>
                        <div className="text-xs text-gray-600 mt-1">Comités TI, présentations, suivi projets</div>
                      </div>
                      <div className="p-3 bg-white rounded-lg shadow-sm">
                        <div className="font-medium text-gray-800 text-sm">Reporting & Documentation</div>
                        <div className="text-xs text-gray-600 mt-1">Rapports mensuels, roadmaps, budgets</div>
                      </div>
                      <div className="p-3 bg-white rounded-lg shadow-sm">
                        <div className="font-medium text-gray-800 text-sm">Communication Multi-Niveaux</div>
                        <div className="text-xs text-gray-600 mt-1">Direction, équipes, fournisseurs</div>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <Lightbulb className="w-5 h-5 text-yellow-600" />
                      Analyse & Décisions
                    </h4>
                    <div className="space-y-3">
                      <div className="p-3 bg-white rounded-lg shadow-sm">
                        <div className="font-medium text-gray-800 text-sm">Analyse de Problèmes</div>
                        <div className="text-xs text-gray-600 mt-1">Diagnostic, solutions, recommandations</div>
                      </div>
                      <div className="p-3 bg-white rounded-lg shadow-sm">
                        <div className="font-medium text-gray-800 text-sm">Prise de Décisions</div>
                        <div className="text-xs text-gray-600 mt-1">Stratégiques, tactiques, techniques</div>
                      </div>
                      <div className="p-3 bg-white rounded-lg shadow-sm">
                        <div className="font-medium text-gray-800 text-sm">Veille Technologique</div>
                        <div className="text-xs text-gray-600 mt-1">Recherche, évaluation, intégration</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'exigences' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-6 rounded-xl border-l-4 border-purple-500">
                <h3 className="text-xl font-bold text-purple-800 mb-4 flex items-center gap-2">
                  <Award className="w-6 h-6 text-purple-600" />
                  Exigences & Compétences
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="p-4 bg-white rounded-lg shadow-sm">
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <BookOpen className="w-5 h-5 text-blue-600" />
                        Formation & Expérience
                      </h4>
                      <ul className="text-sm text-gray-700 space-y-2">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                          <span><strong>Formation:</strong> BAC en TI, gestion ou équivalent</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                          <span><strong>Expérience:</strong> 5+ ans en services TI</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                          <span><strong>Certifications:</strong> ITIL, PMP, NIST (atout)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                          <span><strong>Langues:</strong> Français, Anglais (essentiel)</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="p-4 bg-white rounded-lg shadow-sm">
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <Cpu className="w-5 h-5 text-green-600" />
                        Compétences Techniques
                      </h4>
                      <ul className="text-sm text-gray-700 space-y-2">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                          <span><strong>Cloud:</strong> AWS, Azure, Google Cloud</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                          <span><strong>Sécurité:</strong> NIST, ISO 27001, cybersécurité</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                          <span><strong>Gestion:</strong> Projets, budgets, équipes</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                          <span><strong>IA:</strong> Intégration, éthique, ROI</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'softskills' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-pink-50 to-rose-50 p-6 rounded-xl border-l-4 border-pink-500">
                <h3 className="text-xl font-bold text-pink-800 mb-4 flex items-center gap-2">
                  <Heart className="w-6 h-6 text-pink-600" />
                  💎 VOTRE SUPERPOUVOIR : L'Intelligence Émotionnelle
                </h3>
                <div className="mb-4 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg border border-yellow-300">
                  <p className="text-sm text-yellow-800 font-bold text-center">
                    🌟 <strong>NOTRE SECRET :</strong> "Nous cherchons des <em>GÉNIES ÉMOTIONNELS</em> - Le savoir technique ? L'IA s'en charge ! Votre <em>SUPERPOUVOIR</em> ? L'Intelligence Humaine !" 🌟
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="p-4 bg-white rounded-lg shadow-sm">
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <MessageSquare className="w-5 h-5 text-blue-600" />
                        🎭 Maître de la Communication
                      </h4>
                      <ul className="text-sm text-gray-700 space-y-2">
                        <li className="flex items-start gap-2">
                          <Star className="w-4 h-4 text-yellow-500 mt-0.5" />
                          <span><strong>Communication:</strong> Vous <em>Hypnotisez</em> par votre clarté</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Star className="w-4 h-4 text-yellow-500 mt-0.5" />
                          <span><strong>Leadership:</strong> Vous <em>Inspirez</em> et <em>Transformez</em> les équipes</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Star className="w-4 h-4 text-yellow-500 mt-0.5" />
                          <span><strong>Présentation:</strong> Vous <em>Captivez</em> avec vos histoires</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Star className="w-4 h-4 text-yellow-500 mt-0.5" />
                          <span><strong>Négociation:</strong> Vous <em>Créez des Miracles</em> win-win</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="p-4 bg-white rounded-lg shadow-sm">
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <Brain className="w-5 h-5 text-purple-600" />
                        🧠 Génie Émotionnel
                      </h4>
                      <ul className="text-sm text-gray-700 space-y-2">
                        <li className="flex items-start gap-2">
                          <Star className="w-4 h-4 text-yellow-500 mt-0.5" />
                          <span><strong>Empathie:</strong> Vous <em>Lisez dans les Âmes</em> de vos clients</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Star className="w-4 h-4 text-yellow-500 mt-0.5" />
                          <span><strong>Adaptabilité:</strong> Vous <em>Dansez avec le Changement</em></span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Star className="w-4 h-4 text-yellow-500 mt-0.5" />
                          <span><strong>Créativité:</strong> Vous <em>Créez l'Impossible</em> à partir de rien</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Star className="w-4 h-4 text-yellow-500 mt-0.5" />
                          <span><strong>Intuition:</strong> Vous <em>Voyez l'Invisible</em> avant tout le monde</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Section Finale - Call to Action */}
              <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-8 rounded-xl text-center">
                <h3 className="text-2xl font-bold mb-4">🚀 PRÊT À DEVENIR UNE LÉGENDE ?</h3>
                <p className="text-lg mb-6">
                  Vous n'êtes pas juste un candidat, vous êtes un <strong>FUTUR HÉROS</strong> qui va transformer des vies et créer des empires technologiques !
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="p-4 bg-white bg-opacity-20 rounded-lg">
                    <div className="text-3xl mb-2">💎</div>
                    <div className="font-semibold">Excellence</div>
                    <div className="text-sm opacity-90">Nous ne cherchons que les meilleurs</div>
                  </div>
                  <div className="p-4 bg-white bg-opacity-20 rounded-lg">
                    <div className="text-3xl mb-2">🌟</div>
                    <div className="font-semibold">Impact</div>
                    <div className="text-sm opacity-90">Votre influence changera des vies</div>
                  </div>
                  <div className="p-4 bg-white bg-opacity-20 rounded-lg">
                    <div className="text-3xl mb-2">🚀</div>
                    <div className="font-semibold">Avenir</div>
                    <div className="text-sm opacity-90">Vous construirez l'avenir</div>
                  </div>
                </div>
                <p className="text-xl font-bold">
                  <em>"Rejoignez l'Élite BZ - Où les Rêves deviennent Réalité !"</em>
                </p>
              </div>
            </div>
          )}
        </div>
        </div>

        {/* Footer CTA Pro Sticky */}
        <div className="sticky bottom-0 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 p-6 border-t-4 border-yellow-400 shadow-2xl">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div className="bg-white/20 backdrop-blur-md p-3 rounded-xl border border-white/30">
                <Star className="w-8 h-8 text-yellow-300" />
              </div>
              <div className="text-white">
                <div className="font-black text-xl">BZ Inc.</div>
                <div className="text-blue-100 text-sm">L'intelligence au service de l'excellence TI</div>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-6 py-3 bg-white/20 text-white font-semibold rounded-xl hover:bg-white/30 transition-all border border-white/30 backdrop-blur-sm"
              >
                Fermer
              </button>
              <button
                onClick={handleExportPDF}
                className="flex items-center gap-2 px-6 py-3 bg-white/20 text-white font-semibold rounded-xl hover:bg-white/30 transition-all border border-white/30 backdrop-blur-sm"
              >
                <Download className="w-5 h-5" />
                Export PDF
              </button>
              <button
                className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 font-black rounded-xl hover:from-yellow-300 hover:to-yellow-400 transition-all shadow-xl hover:shadow-2xl hover:scale-105"
              >
                <Sparkles className="w-5 h-5" />
                POSTULER MAINTENANT
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FicheEmploiPrime;
